import React, { useState, useMemo, useCallback } from "react";
import { Container, Box, Grid, ButtonBase, useTheme } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import categorias from "./categorias";
import ImageModal from "../../components/ImageModal";
import VideoCard from "../../components/VideoCard";
import Reveal from "../../components/Reveal";
import { PageHero, Section } from "../../components/layout";
import videos from "./videos";
// Listado generado con scripts/actualizar-galeria.js
import fotos from "./fotos.json";

const HERO_IMAGE =
  "https://ik.imagekit.io/cabanasdelcampo/pileta/20250113_113845.jpg?updatedAt=1737417503152";

// Pide a ImageKit una versión achicada de la foto para la grilla
const thumb = (url, width) => `${url}${url.includes("?") ? "&" : "?"}tr=w-${width}`;

export default function Galeria() {
  const [categoria, setCategoria] = useState(categorias[0]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const theme = useTheme();

  const { exteriores, interiores, pileta, alrededores } = fotos;

  // Combinar todas las imágenes
  const allImages = useMemo(
    () => [...exteriores, ...interiores, ...pileta, ...alrededores],
    [exteriores, interiores, pileta, alrededores]
  );

  // Filtrar imágenes según la categoría seleccionada
  const filteredImages = useMemo(() => {
    switch (categoria) {
      case "Todos":
        return allImages;
      case "Exteriores":
        return exteriores;
      case "Piscina":
        return pileta;
      case "Interiores":
        return interiores;
      case "Alrededores":
        return alrededores;
      default:
        return [];
    }
  }, [allImages, categoria, exteriores, interiores, pileta, alrededores]);

  const total = filteredImages.length;
  const showPrev = useCallback(
    () => setSelectedIndex((i) => (i - 1 + total) % total),
    [total]
  );
  const showNext = useCallback(() => setSelectedIndex((i) => (i + 1) % total), [total]);

  const handleCategoria = (cat) => {
    setCategoria(cat);
    setSelectedIndex(null);
  };

  return (
    <Box>
      <PageHero image={HERO_IMAGE} overline="Galería" title="Un recorrido en imágenes">
        Las cabañas, la pileta, el parque y los paisajes que te esperan en Athos Pampa.
      </PageHero>

      <Section sx={{ pt: { xs: 5, md: 7 }, mb: "-20px" }}>
        <Container>
          {/* Filtros */}
          <Box
            role="tablist"
            aria-label="Categorías de la galería"
            sx={{
              display: "flex",
              gap: 1,
              mb: { xs: 4, md: 5 },
              overflowX: "auto",
              pb: 1,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {categorias.map((cat) => {
              const active = cat === categoria;
              return (
                <ButtonBase
                  key={cat}
                  role="tab"
                  aria-selected={active}
                  onClick={() => handleCategoria(cat)}
                  sx={{
                    flexShrink: 0,
                    px: 2.5,
                    py: 1,
                    borderRadius: 999,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    transition: "background-color .2s ease, color .2s ease, border-color .2s ease",
                    border: `1px solid ${active ? theme.palette.secondary.dark : theme.palette.sand.dark}`,
                    backgroundColor: active ? theme.palette.secondary.dark : "background.paper",
                    color: active ? "white" : "text.primary",
                    "&:hover": {
                      borderColor: theme.palette.secondary.dark,
                    },
                  }}
                >
                  {cat}
                </ButtonBase>
              );
            })}
          </Box>

          {categoria === "Videos" ? (
            <Grid container spacing={3}>
              {videos.map((video) => (
                <Grid item xs={12} sm={6} md={4} key={video.id}>
                  <VideoCard videoUrl={video.url} autoplay={false} muted={false} />
                </Grid>
              ))}
            </Grid>
          ) : (
            // Mosaico: cada foto conserva su proporción
            <Box sx={{ columnCount: { xs: 1, sm: 2, md: 3 }, columnGap: 2 }}>
              {filteredImages.map((image, index) => (
                <Reveal key={image.id} delay={(index % 3) * 60} sx={{ breakInside: "avoid", mb: 2 }}>
                  <ButtonBase
                    onClick={() => setSelectedIndex(index)}
                    aria-label={`Ver foto ${index + 1} en grande`}
                    sx={{
                      display: "block",
                      width: "100%",
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "16px",
                      cursor: "zoom-in",
                      "& img": {
                        width: "100%",
                        height: "auto",
                        display: "block",
                        transition: "transform 1s cubic-bezier(.2,.7,.2,1)",
                      },
                      "&:hover img": { transform: "scale(1.05)" },
                      "&:hover .zoom-icon": { opacity: 1, transform: "scale(1)" },
                    }}
                  >
                    <img
                      src={thumb(image.url, 700)}
                      alt={`Foto ${index + 1} de Cabañas del Campo`}
                      width={image.width}
                      height={image.height}
                      loading="lazy"
                    />
                    <Box
                      className="zoom-icon"
                      sx={{
                        position: "absolute",
                        right: 12,
                        bottom: 12,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        opacity: 0,
                        transform: "scale(0.8)",
                        transition: "opacity .3s ease, transform .3s ease",
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </Box>
                  </ButtonBase>
                </Reveal>
              ))}
            </Box>
          )}
        </Container>
      </Section>

      <ImageModal
        open={selectedIndex !== null}
        onClose={() => setSelectedIndex(null)}
        image={selectedIndex !== null ? { ...filteredImages[selectedIndex], url: thumb(filteredImages[selectedIndex].url, 1800) } : null}
        onPrev={total > 1 ? showPrev : undefined}
        onNext={total > 1 ? showNext : undefined}
        position={selectedIndex !== null ? `${selectedIndex + 1} / ${total}` : undefined}
      />
    </Box>
  );
}
